import { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { COUNTRIES } from '@/lib/countries';

/**
 * Reusable country picker (Feb 2026). Uses shadcn Command inside a
 * Popover so users can type-to-filter — future-proofs the list for
 * when we expand beyond the current handful.
 *
 * Controlled component. `value` is the selected country string or
 * empty. `onChange` is called with the new string. `testId` is required
 * so every consuming form ends up with a unique test hook.
 */
export function CountryCombobox({ value, onChange, testId, placeholder = 'Select country…', disabled = false }) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
          data-testid={testId}
          disabled={disabled}
        >
          {value || <span className="text-muted-foreground">{placeholder}</span>}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search country…" />
          <CommandEmpty>No country found.</CommandEmpty>
          <CommandGroup>
            {COUNTRIES.map((c) => (
              <CommandItem
                key={c}
                value={c}
                onSelect={() => { onChange(c); setOpen(false); }}
                data-testid={`${testId}-option-${c.replace(/\s+/g, '-').toLowerCase()}`}
              >
                <Check className={`mr-2 h-4 w-4 ${value === c ? 'opacity-100' : 'opacity-0'}`} />
                {c}
              </CommandItem>
            ))}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
